import { PlusOutlined } from '@ant-design/icons'
import { Empty, List, Spin, theme, message } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import TestRunItem from './TestRunItem'
import SkeletonTestRunList from './SkeletonTestRunList'
import CreateTestReportModal from './CreateTestReportModal'
import TestCasePoolList from './TestCasePoolList'
import AddTestCaseModal from '../details/testCaseSection/form/AddTestCaseModal'
import ImportTestCasesModal from './ImportTestCasesModal'
import TestRunTabs, {
    getTestRunReportsPath,
    resolveTestRunProjectId,
} from '../../shared/TestRunTabs'
import {
    apiGetTestRunList,
    deleteTestReport,
} from '../../../redux/testRun/testRun.service'
import { PRIVATE_ROUTES } from '../../../../../routes'
import styles from '../../../testrun.module.css'

function computeProgress(item, token) {
    const total = Number(item?.total || 0)
    const passed = Number(item?.passed || 0)
    const failed = Number(item?.failed || 0)
    const retest = Number(item?.retest || 0)
    const pending = Number(item?.pending || 0)
    const pct = (n) => (total ? (n / total) * 100 : 0)

    const percents = {
        pass: pct(passed),
        fail: pct(failed),
        retest: pct(retest),
        pending: pct(pending),
        completed: pct(passed + failed + retest),
    }

    let strokeColor = token?.colorPrimary
    if (total === 0 || pending === total)
        strokeColor = token?.colorTextQuaternary
    if (failed > 0) strokeColor = token?.colorError
    if (passed === total && total > 0) strokeColor = token?.colorSuccess

    return { total, strokeColor, percents }
}

const VIEWS = {
    cases: 'cases',
    reports: 'reports',
}

export default function TestRunList({ projectId: projectIdProp }) {
    const { token } = theme.useToken()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const projectId = resolveTestRunProjectId(projectIdProp)

    const LIMIT = 10
    const tabParam = searchParams.get('tab')
    const activeView =
        tabParam === 'reports' ? VIEWS.reports : VIEWS.cases

    const setActiveView = (view) => {
        if (view === VIEWS.reports) {
            setSearchParams({ tab: 'reports' }, { replace: true })
        } else {
            setSearchParams({}, { replace: true })
        }
    }
    const [items, setItems] = useState([])
    const [skip, setSkip] = useState(0)
    const [loadingInitial, setLoadingInitial] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [isNext, setIsNext] = useState(true)
    const [total, setTotal] = useState(null)
    const [createModalOpen, setCreateModalOpen] = useState(false)
    const [addTestCaseOpen, setAddTestCaseOpen] = useState(false)
    const [importTestCasesOpen, setImportTestCasesOpen] = useState(false)
    const [pendingReport, setPendingReport] = useState({
        testcaseIds: [],
        filter: {},
    })
    const [poolSelectionCount, setPoolSelectionCount] = useState(0)
    const [poolRefreshKey, setPoolRefreshKey] = useState(0)

    const listWrapRef = useRef(null)
    const sentinelRef = useRef(null)
    const observerRef = useRef(null)

    const onEdit = (id) => {
        if (projectId) {
            navigate(
                PRIVATE_ROUTES.projects.projectIdViewTask(
                    projectId,
                    'testrun',
                    id
                )
            )
            return
        }
        navigate(PRIVATE_ROUTES.testRunDetails.replace(':id', id))
    }

    const onDelete = async (id) => {
        if (!id) return
        const resp = await deleteTestReport(id)
        if (resp?.data?.success) {
            message.success(
                resp?.data?.message || 'Test report deleted successfully'
            )
            refreshList()
        } else {
            message.error(
                resp?.data?.message || 'Failed to delete test report'
            )
        }
    }

    const makePayload = useCallback(
        (nextSkip) => ({
            skip: nextSkip,
            limit: LIMIT,
            filter: { project: projectId },
        }),
        [projectId]
    )

    const fetchPage = useCallback(
        async (nextSkip) => {
            const res = await apiGetTestRunList(makePayload(nextSkip))
            const responseData = res?.data ?? {}
            const pageData = Array.isArray(responseData.data)
                ? responseData.data
                : []
            return {
                pageData,
                pageIsNext: responseData.isNext ?? false,
                pageTotal: responseData.count ?? responseData.total ?? null,
            }
        },
        [makePayload]
    )

    const refreshList = useCallback(async () => {
        setLoadingInitial(true)
        try {
            const { pageData, pageIsNext, pageTotal } = await fetchPage(0)
            setItems(pageData || [])
            setIsNext(Boolean(pageIsNext))
            if (pageTotal != null) setTotal(pageTotal)
            setSkip(pageData?.length || 0)
        } finally {
            setLoadingInitial(false)
        }
    }, [fetchPage])

    const handleCreateReportRequest = ({ testcaseIds = [], filter = {} }) => {
        setPendingReport({ testcaseIds, filter })
        setCreateModalOpen(true)
    }

    const handleCreateSuccess = (createdReport) => {
        setPendingReport({ testcaseIds: [], filter: {} })
        refreshList()
        if (createdReport?._id) {
            onEdit(createdReport._id)
            return
        }
        navigate(getTestRunReportsPath(projectId))
    }

    useEffect(() => {
        let mounted = true
            ; (async () => {
                try {
                    setLoadingInitial(true)
                    const { pageData, pageIsNext, pageTotal } = await fetchPage(0)
                    if (!mounted) return
                    setItems(pageData || [])
                    setIsNext(Boolean(pageIsNext))
                    if (pageTotal != null) setTotal(pageTotal)
                    setSkip(pageData?.length || 0)
                } finally {
                    if (mounted) setLoadingInitial(false)
                }
            })()
        return () => {
            mounted = false
        }
    }, [fetchPage])

    const loadMore = useCallback(async () => {
        if (loadingMore || loadingInitial || !isNext) return
        try {
            setLoadingMore(true)
            const { pageData, pageIsNext } = await fetchPage(skip)
            setItems((prev) => [...prev, ...(pageData || [])])
            setIsNext(Boolean(pageIsNext))
            setSkip((prev) => prev + (pageData?.length || 0))
        } finally {
            setLoadingMore(false)
        }
    }, [fetchPage, isNext, loadingInitial, loadingMore, skip])

    useEffect(() => {
        if (activeView !== VIEWS.reports) return
        if (!listWrapRef.current || !sentinelRef.current) return

        observerRef.current?.disconnect()
        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadMore()
            },
            {
                root: listWrapRef.current,
                rootMargin: '0px 0px 200px 0px',
                threshold: 0.01,
            }
        )
        observerRef.current.observe(sentinelRef.current)
        return () => observerRef.current?.disconnect()
    }, [loadMore, items.length, activeView])

    return (
        <div>

            <TestRunTabs
                projectId={projectId}
                activeTab={activeView}
                reportsCount={total}
                selectionCount={poolSelectionCount}
            />

            {activeView === VIEWS.cases ? (
                <TestCasePoolList
                    projectId={projectId}
                    refreshKey={poolRefreshKey}
                    onCreateReport={handleCreateReportRequest}
                    onAddTestCase={() => setAddTestCaseOpen(true)}
                    onImportTestCases={() => setImportTestCasesOpen(true)}
                    onSelectionChange={setPoolSelectionCount}
                />
            ) : loadingInitial ? (
                <SkeletonTestRunList />
            ) : items.length === 0 ? (
                <Empty
                    description="No test reports yet"
                    className={styles.emptyWrap}
                >
                    <div className={styles.emptyActions}>
                        <button
                            type="button"
                            className={styles.createButton}
                            onClick={() => setActiveView(VIEWS.cases)}
                        >
                            <PlusOutlined className={styles.createButtonIcon} />
                            <span>Add test cases, then create a report</span>
                        </button>
                    </div>
                </Empty>
            ) : (
                <div ref={listWrapRef} className={styles.listScroll}>
                    <List
                        dataSource={items}
                        split={false}
                        renderItem={(item) => {
                            const { percents } = computeProgress(item, token)
                            return (
                                <TestRunItem
                                    key={item._id}
                                    item={item}
                                    onDelete={onDelete}
                                    onEdit={onEdit}
                                    percents={percents}
                                />
                            )
                        }}
                    />
                    <div ref={sentinelRef} />
                    <div className={styles.listEndHint}>
                        {loadingMore ? (
                            <Spin size="small" />
                        ) : !isNext && items.length > 10 ? (
                            'No more reports'
                        ) : null}
                    </div>
                </div>
            )}

            <CreateTestReportModal
                isOpen={createModalOpen}
                setOpen={setCreateModalOpen}
                projectId={projectId}
                preselectedTestCaseIds={pendingReport.testcaseIds}
                filterContext={pendingReport.filter}
                onSuccess={handleCreateSuccess}
            />
            <AddTestCaseModal
                isOpen={addTestCaseOpen}
                setOpen={setAddTestCaseOpen}
                projectId={projectId}
                onSuccess={() => setPoolRefreshKey((k) => k + 1)}
            />
            <ImportTestCasesModal
                isOpen={importTestCasesOpen}
                setOpen={setImportTestCasesOpen}
                projectId={projectId}
                onSuccess={() => setPoolRefreshKey((k) => k + 1)}
            />
        </div>
    )
}
